import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// Parser seguro p/ datas que vêm como "YYYY-MM-DD" do <input type="date">.
// JS interpreta "2026-10-30" como UTC, e em fusos a leste/oeste do meridiano
// isso pula um dia (ex.: em -03:00 vira 29/10 às 21h local). Fixamos parseando
// como meio-dia LOCAL, o que torna a data robusta a qualquer fuso brasileiro.
export function parseLocalDate(date) {
  if (date instanceof Date) return date;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(`${date}T12:00:00`);
  }
  return new Date(date);
}

export function formatDate(date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parseLocalDate(date));
}

export function formatShortDate(date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parseLocalDate(date));
}

export function getCountdown(targetDate) {
  const now = new Date();
  const target = parseLocalDate(targetDate);
  const diff = target - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, passed: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    passed: false,
  };
}

export function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export function generateId() {
  return Math.random().toString(36).substr(2, 9);
}

// Valor mínimo de um presente. Antes, a plataforma absorvia a fee fixa do
// Pix (R$1,99), então valores muito baixos davam prejuízo por transação.
// Hoje essa fee é repassada ao convidado (ver ASAAS_GUEST_FEE em
// functions/index.js), então o limite existe só por UX: abaixo disso a fee
// fixa vira uma porcentagem grande demais em cima do presente.
export const MIN_GIFT_VALUE = 50;

export function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

export function formatPhoneBR(value) {
  const d = phoneDigits(value).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// RSVPs enviados ANTES da granularidade por pessoa existir não têm
// `attendees` gravado — mas se o convite é de lista fechada com
// acompanhantes nomeados, dá pra reconstruir: o cadastro (guestList) tem o
// nome de todo mundo, e `attendeeNames` (já existia) lista quem confirmou.
// Cruzando os dois, recupera-se o status pessoa a pessoa sem precisar que o
// convidado reenvie a resposta.
function deriveLegacyAttendees(rsvp, guestList) {
  const guest = (guestList || []).find((g) => g.phoneDigits && g.phoneDigits === phoneDigits(rsvp?.phone));
  const companions = Array.isArray(guest?.companions) ? guest.companions : [];
  if (!guest || companions.length === 0) return null;
  // Sem attendeeNames não dá pra saber quem especificamente confirmou —
  // só reconstrói quando a resposta é "não" (aí sabidamente ninguém vai) ou
  // quando a lista de confirmados foi registrada.
  if (rsvp.willAttend && !Array.isArray(rsvp.attendeeNames)) return null;
  const confirmedNames = new Set(
    (Array.isArray(rsvp.attendeeNames) ? rsvp.attendeeNames : [])
      .map((n) => String(n).replace(/\s*\(criança\)\s*$/i, '').trim().toLowerCase())
  );
  const roster = [{ id: '__self__', name: guest.name, isChild: false }, ...companions];
  return roster.map((a) => ({
    id: a.id,
    name: a.name,
    isChild: !!a.isChild,
    attending: confirmedNames.has(String(a.name).trim().toLowerCase()),
  }));
}

// Um RSVP de convite com acompanhantes nomeados carrega (ou permite
// reconstruir) `attendees` ({ id, name, isChild, attending }[]) com o status
// de cada pessoa. `guestList` (wedding.guestList) é opcional — só é usada
// pra reconstrução de respostas antigas; sem ela, só dados novos.
export function rsvpAttendeesList(rsvp, guestList) {
  if (Array.isArray(rsvp?.attendees) && rsvp.attendees.length > 0) return rsvp.attendees;
  return deriveLegacyAttendees(rsvp, guestList);
}

// Um convite com acompanhantes nomeados pode ter parte confirmada e parte
// não — nesse caso ele aparece nas duas abas (Quem vai / Quem não vai),
// cada uma mostrando só o recorte de pessoas correspondente.
export function rsvpGoesInYesTab(rsvp, guestList) {
  const attendees = rsvpAttendeesList(rsvp, guestList);
  if (attendees) return attendees.some((a) => a.attending);
  return !!rsvp?.willAttend;
}

export function rsvpGoesInNoTab(rsvp, guestList) {
  const attendees = rsvpAttendeesList(rsvp, guestList);
  if (attendees) return attendees.some((a) => !a.attending);
  return !rsvp?.willAttend;
}

// Pessoas efetivamente confirmadas num RSVP: soma por pessoa quando há
// acompanhantes nomeados, senão cai pro par adults/children agregado.
export function rsvpAttendingCounts(rsvp, guestList) {
  const attendees = rsvpAttendeesList(rsvp, guestList);
  if (attendees) {
    const attending = attendees.filter((a) => a.attending);
    return {
      adults: attending.filter((a) => !a.isChild).length,
      children: attending.filter((a) => a.isChild).length,
    };
  }
  if (!rsvp?.willAttend) return { adults: 0, children: 0 };
  return { adults: Math.max(0, Number(rsvp.adults) || 0), children: Math.max(0, Number(rsvp.children) || 0) };
}

// Linhas prontas pra exportar (CSV/PDF) — só convites com pelo menos uma
// pessoa confirmada (ver rsvpGoesInYesTab). Convite parcialmente confirmado
// mostra só quem de fato vai em attendingNames, não o convite inteiro.
// Usado tanto no botão de exportar CSV quanto na view de impressão do PDF,
// pra nunca ter as duas saídas divergindo.
export function buildConfirmedRsvpRows(rsvps, guestList) {
  return (rsvps || [])
    .filter((r) => rsvpGoesInYesTab(r, guestList))
    .map((r) => {
      const attendees = rsvpAttendeesList(r, guestList);
      const counts = rsvpAttendingCounts(r, guestList);
      const attendingNames = attendees
        ? attendees.filter((a) => a.attending).map((a) => (a.isChild ? `${a.name} (criança)` : a.name))
        : [];
      return {
        name: r.name || '',
        phone: r.phone || '',
        adults: counts.adults,
        children: counts.children,
        total: counts.adults + counts.children,
        attendingNames,
        notes: r.notes || '',
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR', { sensitivity: 'base' }));
}
